import ViewerIcon from 'assets/images/others/3d-viewer.svg';
import ComposerIcon from 'assets/images/others/product-configurator.svg';
import CustomizerIcon from 'assets/images/others/visual-customizer.svg';
import { truncate } from 'lodash';
import React, { FC, Suspense, useEffect, useState, useTransition } from 'react';
import { fetchQuery, graphql, useMutation, usePaginationFragment, useRefetchableFragment, useRelayEnvironment } from 'react-relay';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import ConnectModelDialog from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import DuplicateDialog from 'src/components/pages/productConfiguration/steps/6-done/duplicateDialog';
import { Badge } from 'src/components/widgets/badge';
import Loader from 'src/components/widgets/loader';
import UpgradeDialog from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { useLastUsedTool } from 'src/shared/hooks';
import { BulkButton, BulkSelectionSection } from '../../widgets/bulkSelectionButton';
import { ButtonActions, Link, SelectActions, TextButton } from '../../widgets/buttons';
import { MessageDialog, QuestionDialog, useDialogManager } from '../../widgets/dialog';
import { EmptyList } from '../../widgets/emptyList';
import { Icon } from '../../widgets/icon';
import {
    removeNotification,
    showErrorNotification,
    showInfoNotification,
    showNotification,
    showSuccessNotification,
    showWarningNotification
} from '../../widgets/notificationToaster';
import { Spinner } from '../../widgets/spinner';
import { LinkCell, Table, TableContentLoader, TableHeading, useSelectableTable } from '../../widgets/table';
import { ZTooltip } from '../../widgets/ztooltip';

import NoImage from 'assets/images/others/no-image.png';
import config from 'src/shared/config';
import { NoIntegrationDialog, useUpgradeDialogs } from 'src/shared/dialogs';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { ButtonActionsDelete, Center } from 'src/shared/globals';
import { T, backofficeUrl, stringFormat, toGlobalID, toLocalID, uuid } from 'src/shared/helpers';
import { EnvType, useInfiniteScroll } from 'src/shared/hooks';
import useNotFirstRenderEffect from 'src/shared/hooks/useNotFirstRender';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useProductIsResizable } from 'src/shared/hooks/useProductIsResizable';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import VerticalThreePoint from 'src/shared/icons/ellipsis-v-solid.svg';
import EyeSlash from 'src/shared/icons/eye-slash.svg';
import Eye from 'src/shared/icons/eye.svg';
import NotFoundIcon from 'src/shared/icons/icon-not-found.svg';
import SyncIcon from 'src/shared/icons/sync-solid.svg';
import Trash from 'src/shared/icons/trash-alt.svg';
import { WixPublishErrorMessage } from 'src/shared/messages';
import AngleDown from 'src/shared/svg/angleDown';
import styled from 'styled-components';
import { DialogOverlay } from '../dashboard/intro/dialogDel';
import { useProductConfigurationState } from '../productConfiguration/state';
import { ProductColor } from '../productConfiguration/state/model/modelColor';
import AddSalesChannelDialog from '../salesChannels/addSalesChannelDialog';
import { generateStoreNumbers } from '../salesChannels/helpers';
import { SalesChannelNotAvailableDialogWindow } from '../salesChannels/salesChannel';
import { SubscriptionFragmentMaxProductsRefetchQuery } from './__generated__/SubscriptionFragmentMaxProductsRefetchQuery.graphql';
import { modelListProductsPaginationQuery } from './__generated__/modelListProductsPaginationQuery.graphql';
import { productListConnectModelMutation, productListConnectModelMutation$data } from './__generated__/productListConnectModelMutation.graphql';
import { productListDeleteMutation } from './__generated__/productListDeleteMutation.graphql';
import { productListDuplicateMutation, productListDuplicateMutation$data } from './__generated__/productListDuplicateMutation.graphql';
import { productListProductSetStatusMutation } from './__generated__/productListProductSetStatusMutation.graphql';
import { productListProductsDeleteMutation } from './__generated__/productListProductsDeleteMutation.graphql';
import { ProductStatus, productListProductsSetStatusMutation } from './__generated__/productListProductsSetStatusMutation.graphql';
import { productListSubscriptionQuery } from './__generated__/productListSubscriptionQuery.graphql';
import { productListSyncWithEcommerceMutation } from './__generated__/productListSyncWithEcommerceMutation.graphql';
import { ProductProvider, ZakekeEnvironment, productList_models$data, productList_models$key } from './__generated__/productList_models.graphql';
import { productList_subscriptionFragmentMaxProducts$key } from './__generated__/productList_subscriptionFragmentMaxProducts.graphql';
import { productsQuery } from './__generated__/productsQuery.graphql';
import MarketplacePreviewUrlDialog from './marketplacePreviewUrlDialog';
import ProductSalesChannelList, { DisconnectProductSalesChannel } from './productSalesChannelList';
import ProductSidePanel from './productSidebar';
import { DISABLED_PROVIDERS, productProviderIconsList, productProviderNameList } from './utils';

export type Model = Exclude<Exclude<productList_models$data['models'], null>['edges'], null>[0]['node'];
export type AdditionalCodes = Exclude<Model, null>['additionalCodes'];
export type ProductSalesChannel = Exclude<Model, null>['salesChannel'];
type PrintingMethods = Exclude<Model, null>['printingMethods'];

interface Props {
    query: productList_models$key;
    subscriptionFragment: productList_subscriptionFragmentMaxProducts$key;
    salesChannels: productsQuery['response']['salesChannels'];
    search: string;
    count: number;
    salesChannelsIDs?: string[];
    productProvidersIDs?: string[];
    productStatusID?: number | null | undefined;
    ecommerceName?: string;
    handleEmptyList: (isEmpty: boolean) => void;
    orderBy: string;
    onSalesChannelCreated: () => void;
    isShopifyTestStore: boolean;
    environmentSelected: number[];
    userID: number;
    disableProviderProducts?: boolean;
}

interface Store {
    id: string;
    storeName: string | null | undefined;
    storeNumber: number;
    storeUrl?: string | null | undefined;
}

interface NameCellProps {
    name: string;
    id: string; // modelID
    env: ZakekeEnvironment;
    onNameClick: () => void;
}

interface ProductProviderIconProps {
    productProvider: ProductProvider | 'TEMPLATE';
    sourceStores?: Store[];
    salesChannelID?: string;
}

const ProductImage = styled.img`
    border-radius: 5%;
    border: 1px solid rgb(237, 247, 255);
    object-fit: contain;
`;

const CodeCell = styled.div`
    font-size: 14px;
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: flex-start;
    background: white;
    padding-bottom: 30px;
`;

const StyledSpinner = styled(Loader)`
    height: 30px;
`;

const ContainerProviderIcon = styled.div`
    display: flex;
    flex-direction: row;
    align-content: center;
`;

const StyledSpinnerProviderSync = styled(Spinner)`
    height: 20px;
    align-self: center;
`;

const LeftColContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-left: 1rem;
`;

const RightColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    padding-right: 0.5rem;
`;

const ActionsColContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
`;

const SalesChannelColContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    margin-left: 1rem;
    margin-right: 1rem;
    height: 26px;
    line-height: 26px;
    text-align: center;
`;

const PrintingMethodsColContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    width: 100%;
    margin-left: 1rem;
    margin-right: 1rem;
`;

const PrintingMethodsListContainer = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;

    & > * {
        margin-left: 0.2rem;
        margin-right: 0.2rem;
        font-size: 14px;
    }
`;

const PrintingMethodsListPopUpContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    justify-content: flex-start;

    & > * {
        margin-left: 0.2rem;
        margin-right: 0.2rem;
        margin-top: 2px;
        margin-bottom: 2px;
        font-size: 14px;
    }
`;

const NumberAdditionalSC = styled.div`
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    color: ${(props) => props.theme.button.color};
    margin-left: 0.2rem;
    font-weight: bold;
    white-space: nowrap;
    font-family: ${(props) => props.theme.app.font};
    font-size: 14px;
`;

export const StyledBadge = styled(Badge)`
    font-weight: 600;
    width: unset;
    white-space: nowrap;
`;

export const DialogOverlayConnect = styled(DialogOverlay)`
    z-index: 9999;
    justify-content: center;
`;

const SalesChannelClickable = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    border-radius: 4px;
    padding: 2px 6px;

    &:hover {
        background-color: ${(props) => props.theme.skeleton.backgroundColor};
    }
`;

const WrapperAngleDown = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
`;

const TableToolIcon = styled.span`
    display: inline-flex;
    width: 32px;
    height: 32px;

    svg {
        width: 100%;
        height: 100%;
    }
`;

const TableStatusBadge = styled.span<{ $online: boolean }>`
    display: inline-flex;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
    background: ${(p) => (p.$online ? '#d6f3e2' : '#dde6fe')};
    color: ${(p) => (p.$online ? '#065f46' : '#3730a3')};
`;

const TooltipSourceContent = styled.div`
    margin: 0;
    p {
        margin-top: 0;
    }
`;

const nameMaxLength = window.innerWidth < 1900 ? 20 : 30;
const NameCell = (props: NameCellProps) => {
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onNameClick();
    };
    return (
        <LinkCell data-testid={`product-name-cell-${props.id}`}>
            {props.name.length > nameMaxLength ? (
                <ZTooltip position='top' title={T._(props.name, 'Admin')} interactive={true}>
                    <a onClick={handleClick} role='button' aria-label={`Edit product: ${props.name}`} data-testid={`product-name-link-${props.id}`}>
                        {truncate(props.name, { length: nameMaxLength })}
                    </a>
                </ZTooltip>
            ) : (
                <a onClick={handleClick} role='button' aria-label={`Edit product: ${props.name}`} data-testid={`product-name-link-${props.id}`}>
                    {props.name}
                </a>
            )}
        </LinkCell>
    );
};

const getSourceStores = (salesChannels: productsQuery['response']['salesChannels']) => {
    const sourceStores: Store[] = [];

    const salesChannelIDs = salesChannels.filter((x) => x.ecommerceSettings?.ecommerceName !== 'NONE').map((channel) => channel.id || '');

    const storeNumbers = generateStoreNumbers(salesChannelIDs);

    storeNumbers.forEach((store) => {
        const salesChannel = salesChannels.find((x) => x.id === store.salesChannelID);
        if (salesChannel) {
            sourceStores.push({
                id: salesChannel.id,
                storeName: salesChannel.detail?.properties?.name,
                storeNumber: store.storeNumber,
                storeUrl: salesChannel.ecommerceSettings?.definitiveUrl
            });
        }
    });

    return sourceStores;
};

const isMarketplaceProduct = (product: Model) => {
    return (
        product.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY' ||
        product.additionalCodes.some((c) => c.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY')
    );
};

const ProductProviderIcon = (props: ProductProviderIconProps) => {
    const sourceStore = props.salesChannelID && props.sourceStores ? props.sourceStores.find((x) => x.id === props.salesChannelID) : undefined;

    return (
        <ZTooltip
            position='top'
            interactive={true}
            html={
                <>
                    {sourceStore ? (
                        <TooltipSourceContent>
                            <p>{sourceStore.storeName || T._(`Store ${sourceStore.storeNumber}`, 'Admin')}</p>
                            <Link target='_blank' href={sourceStore.storeUrl || ''}>
                                {sourceStore.storeUrl}
                            </Link>
                        </TooltipSourceContent>
                    ) : (
                        <>{T._(productProviderNameList[props.productProvider as keyof typeof productProviderNameList] || props.productProvider, 'Admin')}</>
                    )}
                </>
            }
        >
            <Icon>{productProviderIconsList[props.productProvider as keyof typeof productProviderIconsList] || productProviderIconsList.UNDEFINED}</Icon>
        </ZTooltip>
    );
};

const Image = (props: { url?: string }) => {
    return <ProductImage src={props.url || NoImage} width='58' height='58' data-testid='product-image' />;
};

const subscriptionQuery = graphql`
    query productListSubscriptionQuery {
        subscription {
            maxProducts {
                numPublishedProducts
                maxProducts
            }
        }
    }
`;

export const productsQueryConnection = graphql`
    fragment productList_models on Query
    @refetchable(queryName: "modelListProductsPaginationQuery")
    @argumentDefinitions(
        count: { type: "Int", defaultValue: 20 }
        cursor: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: "" }
        orderBy: { type: "String", defaultValue: "" }
        salesChannelsIDs: { type: "[String!]", defaultValue: null }
        productProvidersIDs: { type: "[String!]", defaultValue: null }
        productStatusID: { type: "Int", defaultValue: null }
    ) {
        models(
            first: $count
            after: $cursor
            search: $search
            orderBy: $orderBy
            salesChannelsIDs: $salesChannelsIDs
            productProvidersIDs: $productProvidersIDs
            productStatusID: $productStatusID
        ) @connection(key: "productsModels_models") {
            __id # This is the cache id
            edges {
                node {
                    ... on Model {
                        id
                        name
                        modelID
                        code
                        providerCode
                        provider
                        productProviderSyncState
                        statusID
                        smallImageUrl
                        zakekeEnvironment
                        canAddAdditionalCode
                        sourceProductTemplateID
                        additionalCodes {
                            code
                            salesChannelID
                            merchantProductName
                            salesChannel {
                                id
                                detail {
                                    id
                                    properties {
                                        name
                                    }
                                }
                                ecommerceSettings {
                                    ecommerceName
                                    remoteUrl
                                    productsApi
                                    hasProductsApi
                                    definitiveUrl
                                }
                            }
                        }
                        modelColorMappings {
                            colorID
                            salesChannelID
                            merchantCode
                        }
                        salesChannel {
                            id
                            detail {
                                id
                                properties {
                                    name
                                }
                            }
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                                productsApi
                                hasProductsApi
                                definitiveUrl
                            }
                        }
                        printingMethods {
                            printTypeID
                            name
                        }
                        colors {
                            colorID
                            name
                            code
                            colorCode
                        }
                    }
                }
            }
        }
    }
`;

const deleteMutation = graphql`
    mutation productListDeleteMutation($connections: [ID!]!, $productID: String!) {
        deleteProduct(productID: $productID) {
            id @deleteEdge(connections: $connections)
        }
    }
`;

const duplicateProduct = graphql`
    mutation productListDuplicateMutation($id: ID!, $targets: [ModelDuplicateTargetInput!]!) {
        duplicateModel(id: $id, targets: $targets) {
            productIDs
            resultMessage
            status
        }
    }
`;

const setStatusMassiveMutation = graphql`
    mutation productListProductsSetStatusMutation($productIDs: [String!]!, $status: ProductStatus!) {
        setStatusProducts(productIDs: $productIDs, status: $status) {
            id
            statusID
        }
    }
`;

const deleteMassiveMutation = graphql`
    mutation productListProductsDeleteMutation($connections: [ID!]!, $productIDs: [String!]!) {
        deleteProducts(productIDs: $productIDs) {
            id @deleteEdge(connections: $connections)
        }
    }
`;

const setStatusMutation = graphql`
    mutation productListProductSetStatusMutation($input: SetStatusProductInput!) {
        setStatusProduct(input: $input) {
            model {
                id
                statusID
            }
            errors {
                ... on Error {
                    message
                }
            }
        }
    }
`;

const subscriptionFragmentMaxProducts = graphql`
    fragment productList_subscriptionFragmentMaxProducts on Query @refetchable(queryName: "SubscriptionFragmentMaxProductsRefetchQuery") {
        subscription {
            id
            areSalesChannelsEnabled
            maxProducts {
                pricingVersion
                canAddProduct
                maxProducts
                numProducts
                numPublishedProducts
            }
        }
    }
`;

const syncWithEcommerceMutation = graphql`
    mutation productListSyncWithEcommerceMutation($productIDs: [String!]!) {
        syncWithEcommerce(productIDs: $productIDs) {
            id
            name
            smallImageUrl
        }
    }
`;

const PrintingMethodsList: FC<{ printingMethods: PrintingMethods }> = ({ printingMethods }) => {
    const maxLength = window.innerWidth < 1900 ? 15 : 20;

    return (
        <PrintingMethodsListContainer>
            {printingMethods.map((printingMethod, i) => {
                return i < 1 && (printingMethod.name.length > maxLength || printingMethods.length > 1) ? (
                    <ZTooltip
                        key={i}
                        html={<PrintingMethodsListPopUp printingMethods={printingMethods}></PrintingMethodsListPopUp>}
                        position={'top'}
                        interactive={true}
                    >
                        <p>{truncate(printingMethod.name, { length: maxLength })}</p>
                    </ZTooltip>
                ) : (
                    <React.Fragment key={i}> {i < 1 && <p>{printingMethod.name}</p>} </React.Fragment>
                );
            })}
            {printingMethods.length > 1 && (
                <>
                    <NumberAdditionalSC>+ {printingMethods.length - 1}</NumberAdditionalSC>
                </>
            )}
        </PrintingMethodsListContainer>
    );
};

const PrintingMethodsListPopUp: FC<{ printingMethods: PrintingMethods }> = ({ printingMethods }) => {
    return (
        <PrintingMethodsListPopUpContainer>
            {printingMethods.map((printingMethod, i) => {
                return <p key={i}>{T._(printingMethod.name, 'Admin')}</p>;
            })}
        </PrintingMethodsListPopUpContainer>
    );
};

const ProductList: FC<Props> = ({
    query,
    subscriptionFragment,
    salesChannels,
    handleEmptyList,
    search,
    orderBy,
    count,
    salesChannelsIDs,
    productProvidersIDs,
    productStatusID,
    ecommerceName,
    onSalesChannelCreated,
    isShopifyTestStore,
    environmentSelected,
    userID,
    disableProviderProducts = false
}) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { setLastUsedTool } = useLastUsedTool();
    const navigate = useNavigate();
    const { dispatch } = useProductConfigurationState();
    const { showPlanUpgradeDialog } = useUpgradeDialogs();
    const { sellerMarketplace } = useSubscriptionData();
    const { isAllowed } = usePermissions();
    const Environment = useRelayEnvironment();

    const { data, loadNext, refetch, hasNext, isLoadingNext } = usePaginationFragment<modelListProductsPaginationQuery, productList_models$key>(
        productsQueryConnection,
        query
    );

    const isNewConfiguratorBackoffice = useFeatureFlagWithConditions('newConfiguratorBackoffice', {
        userId: userID.toString()
    });

    const environmentsSelected = environmentSelected.map((env) => {
        switch (env) {
            case 1:
                return 'CUSTOMIZER';
            case 2:
                return 'COMPOSER';
            case 3:
                return 'VIEWER';
            default:
                return 'UNKNOWN';
        }
    });

    const [subscriptionData, refetchSubscription] = useRefetchableFragment<
        SubscriptionFragmentMaxProductsRefetchQuery,
        productList_subscriptionFragmentMaxProducts$key
    >(subscriptionFragmentMaxProducts, subscriptionFragment);

    const [commitConnectModel, isInFightConnectModel] = useMutation<productListConnectModelMutation>(graphql`
        mutation productListConnectModelMutation($id: ID!, $target: ModelDuplicateTargetInput!) {
            connectModel(id: $id, target: $target)
        }
    `);

    const productList = (data?.models?.edges ?? []).map(({ node }) => node);

    const { showDialog, closeDialog } = useDialogManager();
    const { isResizable } = useProductIsResizable();
    const [isPending, startTransition] = useTransition();
    const [deleteProduct, isInFightDelete] = useMutation<productListDeleteMutation>(deleteMutation);
    const { selectedData, mapRows, headingCheck, resetSelection } = useSelectableTable(productList);
    const [setStatusProductMassive, isInFightUpdateMassive] = useMutation<productListProductsSetStatusMutation>(setStatusMassiveMutation);
    const [deleteProductMassive, isInFightDeleteMassive] = useMutation<productListProductsDeleteMutation>(deleteMassiveMutation);
    const [commitDuplicate, isInFightDuplicate] = useMutation<productListDuplicateMutation>(duplicateProduct);
    const [setStatusProduct, isInFightUpdate] = useMutation<productListProductSetStatusMutation>(setStatusMutation);
    const [syncWithEcommerce, isInFightSync] = useMutation<productListSyncWithEcommerceMutation>(syncWithEcommerceMutation);
    const [isFetchingUpdatedPublishedProductsList, setIsFetchingUpdatedPublishedProductsList] = useState(false);
    const [hasResumed, setHasResumed] = useState(false);
    const [selectedProductId, _setSelectedProductId] = useState<string | null>(() => {
        const sidebarParam = searchParams.get('sidebar');
        return sidebarParam ? toGlobalID(sidebarParam, 'Model') : null;
    });

    const setSelectedProductId = (id: string | null) => {
        _setSelectedProductId(id);
        setSearchParams(
            (prev) => {
                if (id) {
                    prev.set('sidebar', String(toLocalID(id)));
                } else {
                    prev.delete('sidebar');
                }
                return prev;
            },
            { replace: true }
        );
    };
    const getSelectedProduct = () => (selectedProductId ? (productList.find((p) => p.id === selectedProductId) ?? null) : null);

    useInfiniteScroll(() => loadNext(20), hasNext);

    useNotFirstRenderEffect(() => {
        startTransition(() => {
            refetchProducts();
        });
    }, [search, orderBy, salesChannelsIDs, productProvidersIDs, productStatusID]);

    // Re-open sidebar when navigating back from a tool page
    useEffect(() => {
        const { sidebarProductId } = (location.state as any) || {};
        if (sidebarProductId) {
            setSelectedProductId(sidebarProductId);
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    //resume first publishing after integration
    useEffect(() => {
        if (hasResumed) return;

        const { doPublishImmediately, productID } = location.state || {};
        if (!doPublishImmediately || !productID) return;

        const product = productList.find((p) => p.id === toGlobalID(productID, 'Model'));
        if (!product) return;

        handleSetStatusClick(product);

        setHasResumed(true);

        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, productList, hasResumed, navigate]);

    const refetchProducts = () => {
        refetch(
            {
                search: search,
                salesChannelsIDs: salesChannelsIDs,
                productProvidersIDs: productProvidersIDs,
                cursor: '1',
                count: count,
                orderBy: orderBy || 'datedesc',
                productStatusID: productStatusID
            },
            { fetchPolicy: 'network-only' }
        );
        resetSelection();
    };

    const handleSetStatusClick = (product: Model) => {
        if (!isAllowed(['PRODUCTS_PUBLISH'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        const pricingVersion = subscriptionData.subscription?.maxProducts.pricingVersion ?? 'VERSION_1';
        if (pricingVersion === 'VERSION_2') {
            const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
            const numMaxPublishedProducts = subscriptionData.subscription?.maxProducts.maxProducts ?? 0;

            if (product.statusID === 2 && numCurrentPublishedProducts >= numMaxPublishedProducts) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            console.log('upgrade completato, continuo con la pubblicazione');
                            setStatusProduct({
                                variables: {
                                    input: {
                                        productID: product.id,
                                        status: product.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                    }
                                },
                                onError: (error) => {
                                    showErrorNotification({
                                        message: product.statusID === 1 ? T._('Failed Unpublish 1 product', 'Admin') : T._('Failed publish 1 product', 'Admin')
                                    });
                                },
                                onCompleted: (response) => {
                                    if (response.setStatusProduct && response.setStatusProduct.errors && response.setStatusProduct.errors.length > 0) {
                                        const wixError = response.setStatusProduct.errors.find((e) =>
                                            e.message?.includes('The product do not have a customization token')
                                        );

                                        if (wixError) {
                                            showDialog('wix-publish-error', <MessageDialog message={<WixPublishErrorMessage></WixPublishErrorMessage>} />);
                                            return;
                                        }
                                    }

                                    const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
                                    setIsFetchingUpdatedPublishedProductsList(true);

                                    const intervalId = setInterval(() => {
                                        fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {})
                                            .toPromise()
                                            .then((data) => {
                                                const newNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;
                                                if (newNumPublished !== numCurrentPublishedProducts) {
                                                    console.log('blocco il pool, valore aggiornato', newNumPublished);
                                                    clearInterval(intervalId);
                                                    setIsFetchingUpdatedPublishedProductsList(false);
                                                    if (!selectedProductId) {
                                                        showSuccessNotification({
                                                            message:
                                                                product.statusID === 1
                                                                    ? T._('1 Product unpublished', 'Admin')
                                                                    : T._('1 Product published', 'Admin')
                                                        });
                                                    }
                                                    startTransition(() => {
                                                        refetchSubscription({}, { fetchPolicy: 'network-only' });
                                                    });
                                                }
                                            })
                                            .catch(() => {
                                                clearInterval(intervalId);
                                                setIsFetchingUpdatedPublishedProductsList(false);
                                            });
                                    }, 3000);
                                }
                            });
                        }}
                    />
                );
                return;
            }
        }

        if (
            product?.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
            salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE') &&
            product.additionalCodes.length == 0 &&
            product.modelColorMappings.length == 0 &&
            product?.statusID === 2
        ) {
            const realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

            showDialog(
                'connectProductDialog',
                <Suspense
                    fallback={
                        <DialogOverlayConnect>
                            <Loader />
                        </DialogOverlayConnect>
                    }
                >
                    <ConnectModelDialog
                        queryKey={Math.random().toString()}
                        productID={product.id}
                        merchantProductOptionsConnected={[]}
                        forPublishingProduct={true}
                        additionalCodes={product.additionalCodes.map((c) => {
                            return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName ?? '' };
                        })}
                        productColors={product.colors as ProductColor[]}
                        salesChannels={realSalesChannels}
                        productSalesChannelID={product.salesChannel.id}
                        onConfirmMappingVariants={() => {
                            closeDialog('connectProductDialog');
                            setStatusProduct({
                                variables: {
                                    input: {
                                        productID: product.id,
                                        status: product.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                    }
                                },
                                onError: (error) => {
                                    showErrorNotification({
                                        message: product.statusID === 1 ? T._('Failed Unpublish 1 product', 'Admin') : T._('Failed publish 1 product', 'Admin')
                                    });
                                },
                                onCompleted: (response) => {
                                    if (response.setStatusProduct && response.setStatusProduct.errors && response.setStatusProduct.errors.length > 0) {
                                        const wixError = response.setStatusProduct.errors.find((e) =>
                                            e.message?.includes('The product do not have a customization token')
                                        );

                                        if (wixError) {
                                            showDialog('wix-publish-error', <MessageDialog message={<WixPublishErrorMessage></WixPublishErrorMessage>} />);
                                            return;
                                        }
                                    }

                                    refetchProducts();

                                    const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
                                    setIsFetchingUpdatedPublishedProductsList(true);

                                    const intervalId = setInterval(() => {
                                        fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {})
                                            .toPromise()
                                            .then((data) => {
                                                const newNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;
                                                if (newNumPublished !== numCurrentPublishedProducts) {
                                                    console.log('blocco il pool, valore aggiornato', newNumPublished);
                                                    clearInterval(intervalId);
                                                    setIsFetchingUpdatedPublishedProductsList(false);
                                                    if (!selectedProductId) {
                                                        showSuccessNotification({
                                                            message:
                                                                product.statusID === 1
                                                                    ? T._('1 Product unpublished', 'Admin')
                                                                    : T._('1 Product published', 'Admin')
                                                        });
                                                    }
                                                    startTransition(() => {
                                                        refetchSubscription({}, { fetchPolicy: 'network-only' });
                                                    });
                                                }
                                            })
                                            .catch(() => {
                                                clearInterval(intervalId);
                                                setIsFetchingUpdatedPublishedProductsList(false);
                                            });
                                    }, 3000);
                                }
                            });
                        }}
                        onConfirm={async (sourceProduct, salesChannelId) => {
                            if (!product) {
                                showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                                return;
                            }

                            if (!sourceProduct) return;

                            closeDialog('connectProductDialog');

                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('Publishing your customizable product...', 'Admin')
                            });

                            let response!: productListConnectModelMutation$data;

                            try {
                                // Save product
                                response = await new Promise((resolve, reject) =>
                                    commitConnectModel({
                                        onCompleted: (response) => resolve(response),
                                        onError: (response) => reject(response),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        variables: {
                                            id: product.id,
                                            target: {
                                                name: sourceProduct!.name,
                                                sku: sourceProduct!.sku,
                                                imageUrl: sourceProduct!.thumbnail,
                                                salesChannelId
                                            }
                                        }
                                    })
                                );

                                showNotification({
                                    type: 'success',
                                    message: T._('Product connected and published!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });

                                refetchProducts();
                            } catch (ex: any) {
                                showNotification({
                                    type: 'danger',
                                    message: (
                                        <>
                                            {T._('Failed connecting and publishing product.', 'Admin')}
                                            <br />
                                            {ex.errors?.[0].extensions?.message}
                                        </>
                                    ),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            }
                            removeNotification(loadingNotification);
                        }}
                    />
                </Suspense>
            );

            return;
        } else if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    productId={product.modelID}
                    redirectPage={'/products'}
                    subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                    message={
                        <label>
                            {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }

        if (!selectedProductId) {
            showInfoNotification({ message: product.statusID === 1 ? T._('Unpublishing 1 product', 'Admin') : T._('Publishing 1 product', 'Admin') });
        }
        setStatusProduct({
            variables: {
                input: {
                    productID: product.id,
                    status: product.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                }
            },
            onError: (error) => {
                showErrorNotification({
                    message: product.statusID === 1 ? T._('Failed Unpublish 1 product', 'Admin') : T._('Failed publish 1 product', 'Admin')
                });
            },
            onCompleted: (response) => {
                if (response.setStatusProduct && response.setStatusProduct.errors && response.setStatusProduct.errors.length > 0) {
                    const wixError = response.setStatusProduct.errors.find((e) => e.message?.includes('The product do not have a customization token'));

                    if (wixError) {
                        showDialog('wix-publish-error', <MessageDialog message={<WixPublishErrorMessage></WixPublishErrorMessage>} />);
                        return;
                    }
                }

                const numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0;
                setIsFetchingUpdatedPublishedProductsList(true);

                const intervalId = setInterval(() => {
                    fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {})
                        .toPromise()
                        .then((data) => {
                            const newNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;
                            if (newNumPublished !== numCurrentPublishedProducts) {
                                console.log('blocco il pool, valore aggiornato', newNumPublished);
                                clearInterval(intervalId);
                                setIsFetchingUpdatedPublishedProductsList(false);
                                if (!selectedProductId) {
                                    showSuccessNotification({
                                        message: product.statusID === 1 ? T._('1 Product unpublished', 'Admin') : T._('1 Product published', 'Admin')
                                    });
                                }
                                startTransition(() => {
                                    refetchSubscription({}, { fetchPolicy: 'network-only' });
                                });
                            }
                        })
                        .catch(() => {
                            clearInterval(intervalId);
                            setIsFetchingUpdatedPublishedProductsList(false);
                        });
                }, 3000);
            }
        });
    };

    const handleDeleteClick = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to delete products', 'Admin') });
            return;
        }

        showDialog(
            'delete-product',
            <QuestionDialog
                message={stringFormat(T._('Do you want delete the product "{0}"?', 'Admin'), product.name)}
                onYesClick={() => {
                    closeDialog('delete-product');

                    deleteProduct({
                        variables: {
                            connections: data?.models?.__id ? [data.models.__id] : [],
                            productID: product.id
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed deleting product', 'Admin') });
                        },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Product deleted.', 'Admin') });
                            refetchSubscription({}, { fetchPolicy: 'network-only' });
                            resetSelection();
                            if (productList.length <= 1) {
                                handleEmptyList(true);
                            }
                        }
                    });
                }}
            />
        );
    };

    const handleDeleteDirect = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to delete products', 'Admin') });
            return;
        }

        deleteProduct({
            variables: {
                connections: data?.models?.__id ? [data.models.__id] : [],
                productID: product.id
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed deleting product', 'Admin') });
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Product deleted.', 'Admin') });
                refetchSubscription({}, { fetchPolicy: 'network-only' });
                resetSelection();
                if (productList.length <= 1) {
                    handleEmptyList(true);
                }
            }
        });
    };

    const handleDuplicate = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        if (subscriptionData.subscription?.maxProducts.canAddProduct === false) {
            showPlanUpgradeDialog(
                stringFormat(T._('You can configure max {0} products', 'Admin'), (subscriptionData.subscription?.maxProducts.maxProducts || 0).toString())
            );
            return;
        }

        if (product?.salesChannel?.ecommerceSettings?.ecommerceName === 'API' && !product?.salesChannel?.ecommerceSettings?.hasProductsApi) {
            showDialog(
                'no-api-products',
                <MessageDialog
                    message={
                        <p>
                            {T._('For duplicate the product you have to implement the ', 'Admin')}{' '}
                            <Link href={'https://zakeke.zendesk.com/hc/en-us/articles/360010310554-Products-Sync-V3'} target='_blank'>
                                {T._('product sync API', 'Admin')}
                            </Link>
                        </p>
                    }
                />
            );
            return;
        }
        showDialog(
            'duplicate',
            <DuplicateDialog
                sourceProductCode={product.code}
                salesChannelID={product.salesChannel.id}
                onConfirm={async (targets) => {
                    if (targets.length === 0) {
                        showDialog('no-selection', <MessageDialog message={T._('Select at last one product.', 'Admin')} />);
                        return;
                    }

                    closeDialog('duplicate');

                    const loadingNotification = showNotification({
                        type: 'default',
                        message: T._('The product is duplicating...', 'Admin')
                    });

                    let response!: productListDuplicateMutation$data;

                    try {
                        // Save product
                        response = await new Promise<productListDuplicateMutation$data>((resolve, reject) =>
                            commitDuplicate({
                                onCompleted: (response) => resolve(response),
                                onError: (response) => reject(response),
                                updater: (store) => {
                                    store.invalidateStore();
                                    refetchProducts();
                                },
                                variables: {
                                    id: product.id!,
                                    targets: targets.map((x) => ({
                                        name: x.name!,
                                        sku: x.sku!,
                                        imageUrl: x.thumbnail,
                                        salesChannelId: product.salesChannel.id
                                    }))
                                }
                            })
                        );
                        if (response.duplicateModel.status === 'WARNING') {
                            showWarningNotification({
                                message: T._('There is already a duplicating product request in progress. Please wait for it to complete.', 'Admin')
                            });
                        } else {
                            showNotification({
                                type: 'success',
                                message: T._('Products duplicated!', 'Admin'),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        }
                    } catch (ex: any) {
                        console.error(ex);
                        showNotification({
                            type: 'danger',
                            message: (
                                <>
                                    {T._('Failed duplicating products.', 'Admin')}
                                    <br />
                                    {ex.errors?.[0].extensions?.message}
                                </>
                            ),
                            dismiss: {
                                duration: 3000
                            }
                        });
                    }
                    resetSelection();
                    removeNotification(loadingNotification);
                }}
            />
        );
    };

    const handleSetStatusMassiveClick = async (status: ProductStatus) => {
        if (!isAllowed(['PRODUCTS_PUBLISH'])) {
            showErrorNotification({ message: T._('You do not have permission to publish or unpublish products.', 'Admin') });
            return;
        }

        const productsToPublish = selectedData
            .filter((p) => p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0)
            .map((el) => el.id);
        // Filtra e ordina i prodotti da pubblicare , dividendo in due categorie: già pubblicati e non pubblicati
        const productsToPublishPublished = selectedData
            .filter((p) => (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) && p.statusID === 1)
            .map((el) => el.id);

        const productsToPublishUnpublished = selectedData
            .filter((p) => (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) && p.statusID === 2)
            .map((el) => el.id);

        await publishProducts(productsToPublish, status);
    };

    const publishProducts = async (productsToPublish: string[], status: ProductStatus) => {
        const pricingVersion = subscriptionData.subscription?.maxProducts.pricingVersion ?? 'VERSION_1';

        // Filtra e ordina i prodotti da pubblicare , dividendo in due categorie: già pubblicati e non pubblicati
        const productsToPublishPublished = selectedData
            .filter((p) => (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) && p.statusID === 1)
            .map((el) => el.id);

        const productsToPublishUnpublished = selectedData
            .filter((p) => (p.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' || p.additionalCodes.length > 0) && p.statusID !== 1)
            .map((el) => el.id);

        let numCurrentPublishedProducts = subscriptionData.subscription?.maxProducts.numPublishedProducts ?? 0; // 0

        const data = await fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {}).toPromise();
        let updatedNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0; // 0
        let maxProducts = data?.subscription?.maxProducts?.maxProducts; // 50

        const availableSlots = pricingVersion === 'VERSION_2' && maxProducts ? maxProducts - updatedNumPublished : productsToPublish.length;
        const publishableProducts = productsToPublishUnpublished.slice(0, availableSlots); // 2
        const nonPublishableProducts = productsToPublishUnpublished.slice(availableSlots); // 0

        if (status === 'PUBLISHED') {
            // caso 2: hai un numero di prodotti pubblicabili inferiori rispetto a quelli che vuoi pubblicare, divido quindi in due array
            // e avviso che la seconda parte dei prodotti non è stata pubblicata ma può esserlo dopo un upgrade piano
            let productsText = nonPublishableProducts.length === 1 ? 'product' : 'products';

            const infoId = showInfoNotification({
                message: stringFormat(
                    T._('{0} {1} {2}', 'Admin'),
                    status === 'PUBLISHED' ? T._('publishing', 'Admin') : T._('unpublishing', 'Admin'),
                    productsToPublish.length.toString(),
                    productsText
                )
            });

            setStatusProductMassive({
                variables: {
                    // Allora, qui dobbiamo pubblicare tutti i prodotti unpublished che rientrano nel limite (es. di 4 prodotti ne pubblico 2), ma tutti
                    // i prodotti già pubblished, basta ripubblicarli (per fare update ecommerce)
                    productIDs: publishableProducts.concat(productsToPublishPublished),
                    status: status
                },
                onError: () => {
                    removeNotification(infoId);

                    showErrorNotification({
                        message: stringFormat(
                            T._('Failed {0} {1} {2} ', 'Admin'),
                            status === 'PUBLISHED' ? T._('publishing', 'Admin') : T._('unpublishing', 'Admin'),
                            nonPublishableProducts.length.toString(),
                            productsText
                        )
                    });
                },

                onCompleted: () => {
                    removeNotification(infoId);

                    setIsFetchingUpdatedPublishedProductsList(true);
                    let updatedNumPublished = numCurrentPublishedProducts + publishableProducts.length;

                    // Vari casi

                    // 1. Se sono riuscito a pubblicare tutti i prodotti (es. solo prodotti published, oppure published + unpublished ma è tutto ok)
                    if (publishableProducts.length + productsToPublishPublished.length > 0) {
                        showSuccessNotification({
                            message: stringFormat(
                                T._('{0} {1} {2} ', 'Admin'),
                                (publishableProducts.length + productsToPublishPublished.length).toString(),
                                productsText,
                                status === 'PUBLISHED' ? T._('published', 'Admin') : T._('unpublished', 'Admin')
                            )
                        });
                        setIsFetchingUpdatedPublishedProductsList(false);
                    }

                    if (nonPublishableProducts.length > 0) {
                        showErrorNotification({
                            message: stringFormat(
                                T._('Failed {0} {1} {2} ', 'Admin'),
                                status === 'PUBLISHED' ? T._('publishing', 'Admin') : T._('unpublishing', 'Admin'),
                                nonPublishableProducts.length.toString(),
                                productsText
                            )
                        });
                    }

                    if (publishableProducts.length === 0 && productsToPublishPublished.length === 0) {
                        showDialog(
                            'Upgrade_Plan',
                            <UpgradeDialog
                                redirectUrlFromDashboard={encodeURI(window.location.href)}
                                marketPlace={sellerMarketplace ?? undefined}
                                message={
                                    <>
                                        {stringFormat(
                                            T._(
                                                `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                                'Admin'
                                            ),
                                            maxProducts!.toString()
                                        )}
                                    </>
                                }
                                onUpgradeCompleted={async () => {
                                    publishProducts(productsToPublishUnpublished, status);
                                }}
                            />
                        );
                    } else if (nonPublishableProducts.length > 0) {
                        showDialog(
                            'Upgrade_Plan',
                            <UpgradeDialog
                                redirectUrlFromDashboard={encodeURI(window.location.href)}
                                marketPlace={sellerMarketplace ?? undefined}
                                message={
                                    <>
                                        {stringFormat(
                                            T._(
                                                `Your current plan allows for {0} published products. We've published {1} products, but {2} items couldn't be published as they exceed your product limit. Upgrade your plan to increase your published product slots.`,
                                                'Admin'
                                            ),
                                            maxProducts!.toString(),
                                            (publishableProducts.length + productsToPublishPublished.length).toString(),
                                            nonPublishableProducts.length.toString()
                                        )}
                                    </>
                                }
                                onUpgradeCompleted={async () => {
                                    publishProducts(productsToPublishUnpublished, status);
                                }}
                            />
                        );
                    }

                    if (publishableProducts.length === 0) {
                        setIsFetchingUpdatedPublishedProductsList(false);
                        return;
                    }

                    const intervalId = setInterval(async () => {
                        try {
                            const data = await fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {}).toPromise();
                            updatedNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;

                            if (updatedNumPublished !== numCurrentPublishedProducts) {
                                clearInterval(intervalId);
                                numCurrentPublishedProducts = updatedNumPublished;
                            }
                        } catch {
                            clearInterval(intervalId);
                            setIsFetchingUpdatedPublishedProductsList(false);
                        }
                    }, 3000);
                }
            });
        }
        // sono unpublish, posso procedere senza check
        else {
            // caso 3: la pubblicazione può procedere in quanto il limite di prodotti pubblicati non è stato raggiunto
            let publishingOrUnpublishingProducts = 'unpublishing';
            let numPublishingOrUnpublishingProducts = productsToPublish.length;
            let productsText = numPublishingOrUnpublishingProducts === 1 ? 'product' : 'products';

            showInfoNotification({
                message: stringFormat(
                    T._('{0} {1} {2}', 'Admin'),
                    publishingOrUnpublishingProducts,
                    numPublishingOrUnpublishingProducts.toString(),
                    productsText
                )
            });

            setStatusProductMassive({
                variables: {
                    productIDs: productsToPublish,
                    status: status
                },
                onError: () => {
                    showErrorNotification({
                        message: stringFormat(
                            T._('Failed {0} {1} {2} ', 'Admin'),
                            numPublishingOrUnpublishingProducts.toString(),
                            productsText,
                            publishingOrUnpublishingProducts
                        )
                    });
                },

                onCompleted: () => {
                    setIsFetchingUpdatedPublishedProductsList(false);
                    setIsFetchingUpdatedPublishedProductsList(true);
                    let updatedNumPublished = numCurrentPublishedProducts;

                    const intervalId = setInterval(async () => {
                        try {
                            const data = await fetchQuery<productListSubscriptionQuery>(Environment, subscriptionQuery, {}).toPromise();
                            updatedNumPublished = data?.subscription?.maxProducts?.numPublishedProducts ?? 0;

                            if (updatedNumPublished !== numCurrentPublishedProducts) {
                                console.log('blocco il pool, valore aggiornato', updatedNumPublished);
                                clearInterval(intervalId);
                                numCurrentPublishedProducts = updatedNumPublished;
                                setIsFetchingUpdatedPublishedProductsList(false);
                                showSuccessNotification({
                                    message: stringFormat(
                                        T._('{0} {1} {2} ', 'Admin'),
                                        numPublishingOrUnpublishingProducts.toString(),
                                        productsText,
                                        'unpublished'
                                    )
                                });
                            }
                        } catch {
                            clearInterval(intervalId);
                            setIsFetchingUpdatedPublishedProductsList(false);
                        }
                    }, 3000);
                }
            });
        }
    };

    const handleOnlickMassiveDelete = () => {
        if (!isAllowed(['PRODUCTS_DELETE'])) {
            showErrorNotification({ message: T._('You do not have permission to delete products.', 'Admin') });
            return;
        }

        showDialog(
            'delete-products',
            <QuestionDialog
                message={stringFormat(T._('Do you want delete {0} products?', 'Admin'), selectedData.length.toString())}
                onYesClick={() => {
                    closeDialog('delete-products');
                    // ci vuole lo scroll on top
                    deleteProductMassive({
                        variables: {
                            connections: data?.models?.__id ? [data.models.__id] : [],
                            productIDs: selectedData.map((el) => el.id)
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed deleting products', 'Admin') });
                        },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Products deleted.', 'Admin') });
                            resetSelection();
                            refetchSubscription({}, { fetchPolicy: 'network-only' });
                            resetSelection();
                            if ((subscriptionData.subscription?.maxProducts.numProducts ?? 0) == 0) {
                                handleEmptyList(true);
                            }
                        }
                    });
                }}
            />
        );
    };

    const handleAddSalesChannels = (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        let realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

        // if (product.sourceProductTemplateID) {
        if (realSalesChannels.length == 0) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                    message={
                        <label>
                            {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        } else if (realSalesChannels.length == 1) {
            isShopifyTestStore
                ? showDialog(
                      'add-sales-channel-not-available',
                      <MessageDialog
                          windowDecorator={SalesChannelNotAvailableDialogWindow}
                          message={
                              <>
                                  {T._(
                                      "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                      'Admin'
                                  )}
                                  <br />
                                  <br />
                                  {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                                  <br />
                                  <br />
                                  {T._('Thank you for your understanding.', 'Admin')}
                              </>
                          }
                          buttons={[
                              {
                                  label: T._('OK', 'Admin'),
                                  onClick: () => {
                                      closeDialog('add-sales-channel-not-available');
                                  }
                              }
                          ]}
                      />
                  )
                : showDialog(
                      'add-sales-channel',
                      <AddSalesChannelDialog
                          onSalesChannelCreated={() => {
                              closeDialog('add-sales-channel');
                              onSalesChannelCreated();
                          }}
                      ></AddSalesChannelDialog>
                  );
        } else {
            let availableSalesChannels = realSalesChannels.filter(
                (rs) => rs.id != product.salesChannel.id && !product.additionalCodes.some((ac) => ac.salesChannelID == rs.id)
            );

            if (availableSalesChannels.length == 0) {
                showNotification({
                    type: 'danger',
                    message: T._('The product has been connected with all sales channels.', 'Admin'),
                    dismiss: {
                        duration: 3000
                    }
                });
                return;
            }

            showDialog(
                'connectProductDialog',
                <Suspense
                    fallback={
                        <DialogOverlayConnect>
                            <Loader />
                        </DialogOverlayConnect>
                    }
                >
                    <ConnectModelDialog
                        productID={'guid://Zakeke/' + uuid() + '/' + toLocalID(product.id)}
                        forEditProduct={false}
                        forAddingNewSalesChannel={true}
                        merchantProductOptionsConnected={[]}
                        additionalCodes={product.additionalCodes.map((c) => {
                            return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName ?? '' };
                        })}
                        productColors={product.colors as ProductColor[]}
                        salesChannels={availableSalesChannels}
                        productSalesChannelID={product.salesChannel.id}
                        onConfirm={async (sourceProduct, salesChannelId) => {
                            if (!product) {
                                showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                                return;
                            }

                            if (!sourceProduct) return;

                            closeDialog('connectProductDialog');

                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('Publishing your customizable product...', 'Admin')
                            });

                            let response!: productListConnectModelMutation$data;

                            try {
                                // Save product
                                response = await new Promise((resolve, reject) =>
                                    commitConnectModel({
                                        onCompleted: (response) => resolve(response),
                                        onError: (response) => reject(response),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        variables: {
                                            id: product.id,
                                            target: {
                                                name: sourceProduct!.name,
                                                sku: sourceProduct!.sku,
                                                imageUrl: sourceProduct!.thumbnail,
                                                salesChannelId
                                            }
                                        }
                                    })
                                );

                                showNotification({
                                    type: 'success',
                                    message: T._('Product connected and published!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });

                                refetchProducts();
                            } catch (ex: any) {
                                showNotification({
                                    type: 'danger',
                                    message: (
                                        <>
                                            {T._('Failed connecting and publishing product.', 'Admin')}
                                            <br />
                                            {ex.errors?.[0].extensions?.message}
                                        </>
                                    ),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            }
                            removeNotification(loadingNotification);
                        }}
                    />
                </Suspense>
            );
        }
    };

    const handleMouseCickTest = (product: Model) => {
        if (!isAllowed(['ORDERS'])) {
            showErrorNotification({ message: T._('You must have the ability to also access orders to test a product and create a test order.', 'Admin') });
            return;
        }

        let linkTest = '/test-mode/' + toLocalID(product.id);
        navigate(backofficeUrl(linkTest));
    };

    const handleSyncWithEcommerceClick = (productIDs: string[]) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        syncWithEcommerce({
            variables: {
                productIDs: productIDs
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed syncing product', 'Admin') });
            },
            onCompleted: () => {
                showSuccessNotification({ message: T._('Product image/name synced', 'Admin') });
            }
        });
    };

    const handleMarketplacePreviewClick = (productID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog('marketplace-preview-url', <MarketplacePreviewUrlDialog productID={productID} />);
    };

    const handleEdit = async (product: Model) => {
        const env = product.zakekeEnvironment as EnvType;

        console.log('handleEdit', product);
        dispatch({ type: 'LOAD_PRODUCT', model: product });

        const link = '/product?product_id=' + toLocalID(product.id);
        const linkViewer = '/' + config.culture + '/Admin/DAM?page=/gallery';
        const linkComposer = '/' + config.culture + '/Composer?page=product&id=' + toLocalID(product.id);

        console.log('🔧 handleEdit - Environment:', product.zakekeEnvironment, 'Feature flag active:', isNewConfiguratorBackoffice);

        // Logica basata su feature flag
        if (!isNewConfiguratorBackoffice) {
            // FF DISATTIVO: usa i link diretti senza settare lastUsedTool
            switch (product.zakekeEnvironment) {
                case 'VIEWER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF disabled, VIEWER -> linkViewer:', linkViewer);
                    window.location.href = linkViewer;
                    break;
                case 'COMPOSER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF disabled, COMPOSER -> linkComposer:', linkComposer);
                    window.location.href = linkComposer;
                    break;
                case 'CUSTOMIZER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF disabled, CUSTOMIZER -> backoffice link:', link);
                    navigate(backofficeUrl(link));
                    break;
                default:
                    navigate(backofficeUrl(link));
                    break;
            }
        } else {
            // FF ATTIVO: setta sempre il lastUsedTool appropriato
            console.log('product.zakekeEnvironment', product.zakekeEnvironment);
            switch (product.zakekeEnvironment) {
                case 'VIEWER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, VIEWER -> setting tool 3 + linkViewer:', linkViewer);
                    setLastUsedTool('VIEWER');
                    window.location.href = linkViewer;
                    break;
                case 'COMPOSER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, COMPOSER -> setting tool 1 + backoffice:', link);
                    setLastUsedTool('COMPOSER');
                    navigate(backofficeUrl(link));
                    break;
                case 'CUSTOMIZER':
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, CUSTOMIZER -> setting tool 1 + backoffice:', link);
                    setLastUsedTool('CUSTOMIZER');
                    navigate(backofficeUrl(link));
                    break;
                default:
                    console.log('🔧 lastUsedTool, productList.handleEdit - FF active, DEFAULT -> backoffice:', link);
                    setLastUsedTool('CUSTOMIZER');
                    navigate(backofficeUrl(link));
                    break;
            }
        }
    };

    const handleUpdateVariants = async (product: Model) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        const productIsResizable = await isResizable(product.id);

        if (productIsResizable === null) {
            return;
        }

        if (productIsResizable) {
            showErrorNotification({ message: T._('You cannot add variants to a resizable product.', 'Admin') });
            return;
        }

        const link = '/product/variations/?product_id=' + toLocalID(product.id);
        navigate(backofficeUrl(link));
    };

    const sourceStores = getSourceStores(salesChannels);

    const headings = [
        [headingCheck, '45px'],
        ['', '85px'],
        [T._('Name', 'Admin'), '1.2fr'],
        [T._('Code', 'Admin'), '0.8fr'],
        [<LeftColContainer>{T._('Tool', 'Admin')}</LeftColContainer>, '0.4fr'],
        [<LeftColContainer>{T._('Status', 'Admin')}</LeftColContainer>, '0.6fr'],
        [<LeftColContainer>{T._('Source', 'Admin')}</LeftColContainer>, '0.6fr'],
        [<LeftColContainer>{T._('Sales channels', 'Admin')}</LeftColContainer>, '0.8fr'],
        [<LeftColContainer>{T._('Print Methods', 'Admin')}</LeftColContainer>, '0.7fr'],
        <RightColContainer></RightColContainer>
    ] as TableHeading[];

    const getBulkButtons = () => {
        let buttons = [
            {
                key: 'info',
                label: selectedData.length + (selectedData.length > 1 ? T._(' rows ', 'Admin') : T._(' row ', 'Admin')) + T._('selected', 'Admin')
            } as BulkButton
        ];

        if (
            ecommerceName &&
            ecommerceName !== 'NONE' &&
            !selectedData.some((p) => p.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' && p.additionalCodes.length == 0)
        ) {
            buttons = [
                ...buttons,
                {
                    key: 'publish',
                    label: T._('Publish', 'Admin'),
                    tooltip: T._('Publish all selected products to your store', 'Admin'),
                    icon: <Eye />,
                    onClick: (e) => handleSetStatusMassiveClick('PUBLISHED'),
                    disabled: isInFightUpdateMassive || isFetchingUpdatedPublishedProductsList
                } as BulkButton,
                {
                    key: 'unpublish',
                    label: T._('Unpublish', 'Admin'),
                    tooltip: T._('Remove all selected products from your store', 'Admin'),
                    icon: <EyeSlash />,
                    onClick: (e) => handleSetStatusMassiveClick('UN_PUBLISHED'),
                    disabled: isInFightUpdateMassive || isFetchingUpdatedPublishedProductsList
                } as BulkButton
            ];
        }

        if (
            (ecommerceName === 'SHOPIFY' || ecommerceName === 'WOO_COMMERCE' || ecommerceName === 'BIG_COMMERCE' || ecommerceName === 'MAGENTO') &&
            !selectedData.some((p) => p.salesChannel.ecommerceSettings?.ecommerceName === 'NONE')
        ) {
            buttons = [
                ...buttons,
                {
                    key: 'sync',
                    label: T._('Sync product image/name with e-commerce', 'Admin'),
                    tooltip: T._('Sync image/name with e-commerce', 'Admin'),
                    icon: <SyncIcon />,
                    onClick: (e) => handleSyncWithEcommerceClick(selectedData.map((el) => el.id)),
                    disabled: isInFightSync
                } as BulkButton
            ];
        }
        if (!selectedData.some((p) => p.zakekeEnvironment === 'VIEWER')) {
            buttons = [
                ...buttons,
                {
                    key: 'delete',
                    label: T._('Delete', 'Admin'),
                    tooltip: T._('Delete the product from your list', 'Admin'),
                    icon: <Trash />,
                    onClick: (e) => handleOnlickMassiveDelete(),
                    disabled: isInFightDeleteMassive || isInFightUpdateMassive
                } as BulkButton
            ];
        }
        return buttons;
    };

    const shouldBypassToolFilter = environmentsSelected.length === 0;

    return (
        <>
            {!isPending && !isInFightDelete && !isInFightDeleteMassive && !isInFightDuplicate && !isInFightSync && (
                <>
                    {selectedData.length > 0 && (
                        <ButtonContainer data-testid='bulk-actions-container'>
                            <BulkSelectionSection buttons={getBulkButtons()} />
                            {isInFightUpdateMassive && <StyledSpinner />}
                        </ButtonContainer>
                    )}
                    {productList.length === 0 && search.length > 0 && (
                        <EmptyList
                            data-testid='empty-list-search-no-results'
                            icon={search.length > 0 ? <NotFoundIcon /> : null}
                            title={T._('No products found', 'Admin')}
                            subTitle={T._('Sorry, no product was found for your search. Please try using different keywords.', 'Admin')}
                        />
                    )}
                    {productList.length > 0 && (
                        <Table headings={headings} data-testid='products-table' compact>
                            {mapRows(
                                shouldBypassToolFilter
                                    ? productList
                                    : productList.filter((product) => environmentsSelected.includes(product.zakekeEnvironment)),
                                (product: Model) => {
                                    return [
                                        <Image url={product.smallImageUrl}></Image>,
                                        <NameCell
                                            name={product.name}
                                            id={product.modelID.toString()}
                                            env={product.zakekeEnvironment}
                                            onNameClick={() => {
                                                if (product.zakekeEnvironment === 'COMPOSER') {
                                                    window.location.href = `/Composer?id=${product.modelID}&page=product`;
                                                } else if (product.zakekeEnvironment === 'VIEWER') {
                                                    window.location.href = '/' + config.culture + '/Admin/DAM?page=/gallery';
                                                } else {
                                                    navigate(backofficeUrl('/product?product_id=' + product.modelID));
                                                }
                                            }}
                                        />,
                                        product.zakekeEnvironment !== 'VIEWER' && product.code.length < 10 ? (
                                            <CodeCell data-testid={`product-code-${product.modelID}`}>{product.code}</CodeCell>
                                        ) : (
                                            <ZTooltip position='top' title={T._(product.code, 'Admin')} interactive={true}>
                                                <CodeCell data-testid={`product-code-${product.modelID}`}>{truncate(product.code, { length: 10 })}</CodeCell>
                                            </ZTooltip>
                                        ),
                                        <LeftColContainer data-testid={`product-tool-${product.modelID}`}>
                                            <ZTooltip
                                                position='top'
                                                title={
                                                    product.zakekeEnvironment === 'CUSTOMIZER'
                                                        ? 'Customizer'
                                                        : product.zakekeEnvironment === 'COMPOSER'
                                                          ? 'Configurator'
                                                          : 'Viewer'
                                                }
                                            >
                                                <TableToolIcon>
                                                    {product.zakekeEnvironment === 'CUSTOMIZER' ? (
                                                        <CustomizerIcon />
                                                    ) : product.zakekeEnvironment === 'COMPOSER' ? (
                                                        <ComposerIcon />
                                                    ) : (
                                                        <ViewerIcon />
                                                    )}
                                                </TableToolIcon>
                                            </ZTooltip>
                                        </LeftColContainer>,
                                        <LeftColContainer data-testid={`product-status-${product.modelID}`}>
                                            <TableStatusBadge $online={product.statusID === 1}>
                                                {product.statusID === 1 ? T._('Online', 'Admin') : T._('Not published', 'Admin')}
                                            </TableStatusBadge>
                                        </LeftColContainer>,
                                        <LeftColContainer data-testid={`product-source-${product.modelID}`}>
                                            <ProductProviderIcon
                                                productProvider={
                                                    product.sourceProductTemplateID != null
                                                        ? 'TEMPLATE'
                                                        : product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' && product.provider !== 'HP'
                                                          ? 'UNDEFINED'
                                                          : product.provider
                                                }
                                                sourceStores={sourceStores}
                                                salesChannelID={product.salesChannel.id}
                                            />

                                            {product.productProviderSyncState == 0 && (
                                                <ZTooltip
                                                    position='top'
                                                    interactive={true}
                                                    html={
                                                        <span>
                                                            {T._(
                                                                'Product is syncing. Refresh page to update. If after a few hours, the product is still not synced, read ',
                                                                'Admin'
                                                            )}
                                                            <Link
                                                                href='https://zakeke.zendesk.com/hc/en-us/articles/10982534777756-Why-my-Printful-products-are-unsynced'
                                                                target='_blank'
                                                            >
                                                                {T._('this article', 'Admin')}
                                                            </Link>
                                                            .
                                                        </span>
                                                    }
                                                >
                                                    <StyledSpinnerProviderSync />
                                                </ZTooltip>
                                            )}
                                        </LeftColContainer>,

                                        <SalesChannelColContainer data-testid={`product-sales-channels-${product.modelID}`}>
                                            {product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
                                                !product.additionalCodes.some((ac) => ac.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE') &&
                                                T._('None', 'Admin')}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
                                                product.additionalCodes.length === 1 &&
                                                product.additionalCodes.length}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' && product.additionalCodes.length > 1 && (
                                                <SalesChannelClickable
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showDialog(
                                                            'product-sales-channels',
                                                            <ProductSalesChannelList
                                                                additionalCodes={product.additionalCodes}
                                                                code={product.code}
                                                                productID={product.id}
                                                                salesChannel={product.salesChannel}
                                                            />
                                                        );
                                                    }}
                                                    data-testid={`sales-channels-dropdown-${product.modelID}`}
                                                >
                                                    <span data-testid={`sales-channels-count-${product.modelID}`}>{product.additionalCodes?.length}</span>
                                                    <WrapperAngleDown>
                                                        <AngleDown />
                                                    </WrapperAngleDown>
                                                </SalesChannelClickable>
                                            )}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && product.additionalCodes?.length === 0 && (
                                                <span data-testid={`sales-channels-single-${product.modelID}`}>{1}</span>
                                            )}
                                            {product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && product.additionalCodes?.length > 0 && (
                                                <SalesChannelClickable
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showDialog(
                                                            'product-sales-channels',
                                                            <ProductSalesChannelList
                                                                additionalCodes={product.additionalCodes}
                                                                code={product.code}
                                                                productID={product.id}
                                                                salesChannel={product.salesChannel}
                                                            />
                                                        );
                                                    }}
                                                    data-testid={`sales-channels-multiple-dropdown-${product.modelID}`}
                                                >
                                                    <span data-testid={`sales-channels-multiple-count-${product.modelID}`}>
                                                        {product.additionalCodes?.length + 1}
                                                    </span>
                                                    <WrapperAngleDown>
                                                        <AngleDown />
                                                    </WrapperAngleDown>
                                                </SalesChannelClickable>
                                            )}
                                        </SalesChannelColContainer>,

                                        <PrintingMethodsColContainer data-testid={`product-printing-methods-${product.modelID}`}>
                                            {product.zakekeEnvironment !== 'VIEWER' && product.printingMethods && product.printingMethods.length > 0 && (
                                                <PrintingMethodsList printingMethods={product.printingMethods} />
                                            )}
                                        </PrintingMethodsColContainer>,

                                        <ActionsColContainer data-testid={`product-actions-${product.modelID}`}>
                                            {(product.zakekeEnvironment === 'CUSTOMIZER' || product.zakekeEnvironment === 'COMPOSER') && (
                                                <TextButton
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMouseCickTest(product);
                                                    }}
                                                    data-testid={`preview-button-${product.modelID}`}
                                                >
                                                    {T._('Preview', 'Admin')}
                                                </TextButton>
                                            )}
                                            <SelectActions
                                                key={'selectAction_' + product.modelID}
                                                data-testid={`actions-menu-${product.modelID}`}
                                                header={
                                                    <Icon $hoverable data-testid={`actions-menu-icon-${product.modelID}`}>
                                                        <VerticalThreePoint />
                                                    </Icon>
                                                }
                                                items={[
                                                    <ButtonActions
                                                        key={'details_button_' + product.modelID}
                                                        onClick={() => setSelectedProductId(product.id)}
                                                        data-testid={`details-button-${product.modelID}`}
                                                    >
                                                        {T._('Product details', 'Admin')}
                                                    </ButtonActions>,
                                                    <ButtonActions
                                                        key={'edit_button_' + product.modelID}
                                                        onClick={() => handleEdit(product)}
                                                        data-testid={`edit-button-${product.modelID}`}
                                                    >
                                                        {T._('Edit product', 'Admin')}
                                                    </ButtonActions>,
                                                    product.zakekeEnvironment !== 'VIEWER' && (
                                                        <ButtonActions
                                                            disabled={isInFightUpdateMassive || isFetchingUpdatedPublishedProductsList}
                                                            key={'setstatus_button_' + product.modelID}
                                                            onClick={() => handleSetStatusClick(product)}
                                                            data-testid={`publish-unpublish-button-${product.modelID}`}
                                                        >
                                                            {product.statusID === 1
                                                                ? T._('Unpublish', 'Admin')
                                                                : !salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')
                                                                  ? T._('Connect and publish', 'Admin')
                                                                  : T._('Publish', 'Admin')}
                                                        </ButtonActions>
                                                    ),
                                                    product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
                                                        product.zakekeEnvironment !== 'VIEWER' &&
                                                        product.provider !== 'INVITION' &&
                                                        product.provider !== 'CUSTOM_CAT' &&
                                                        product.provider !== 'PRINTFUL' &&
                                                        (product.zakekeEnvironment === 'CUSTOMIZER' ||
                                                            (product.zakekeEnvironment === 'COMPOSER' && isNewConfiguratorBackoffice)) && (
                                                            <ButtonActions
                                                                key={'duplicate_button_' + product.modelID}
                                                                onClick={() => handleDuplicate(product)}
                                                                data-testid={`duplicate-button-${product.modelID}`}
                                                            >
                                                                {T._('Duplicate', 'Admin')}
                                                            </ButtonActions>
                                                        ),
                                                    product.canAddAdditionalCode &&
                                                        (product.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' ||
                                                            product.additionalCodes.length >= 1) && (
                                                            <ButtonActions
                                                                key={'addChannel_button_' + product.modelID}
                                                                onClick={() => handleAddSalesChannels(product)}
                                                                data-testid={`add-sales-channel-button-${product.modelID}`}
                                                            >
                                                                {T._('Add to a Sales Channel', 'Admin')}
                                                            </ButtonActions>
                                                        ),
                                                    product.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
                                                        product.additionalCodes.length === 1 && (
                                                            <ButtonActions
                                                                key={'disconnectSalesChannel_button_' + product.modelID}
                                                                onClick={() => {
                                                                    showDialog(
                                                                        'delete-channel',
                                                                        <DisconnectProductSalesChannel
                                                                            productID={product.id}
                                                                            sku={product.additionalCodes[0].code}
                                                                            salesChannelID={product.additionalCodes[0].salesChannelID}
                                                                            onRemoveCompleted={() => {
                                                                                refetchProducts();
                                                                            }}
                                                                        ></DisconnectProductSalesChannel>
                                                                    );
                                                                }}
                                                                data-testid={`disconnect-sales-channel-button-${product.modelID}`}
                                                            >
                                                                {T._('Disconnect from Sales Channel', 'Admin')}
                                                            </ButtonActions>
                                                        ),
                                                    product.zakekeEnvironment === 'CUSTOMIZER' &&
                                                        product.provider !== 'INVITION' &&
                                                        product.provider !== 'CUSTOM_CAT' &&
                                                        product.provider !== 'PRINTFUL' && (
                                                            <ButtonActions
                                                                key={'updateVariations_button_' + product.modelID}
                                                                onClick={async () => await handleUpdateVariants(product)}
                                                                data-testid={`update-variants-button-${product.modelID}`}
                                                            >
                                                                {T._('Add or update variants', 'Admin')}
                                                            </ButtonActions>
                                                        ),
                                                    (product.zakekeEnvironment === 'COMPOSER' || product.zakekeEnvironment === 'CUSTOMIZER') &&
                                                        (product.salesChannel?.ecommerceSettings?.ecommerceName === 'SHOPIFY' ||
                                                            product.salesChannel?.ecommerceSettings?.ecommerceName === 'WOO_COMMERCE' ||
                                                            product.salesChannel?.ecommerceSettings?.ecommerceName === 'BIG_COMMERCE' ||
                                                            product.salesChannel?.ecommerceSettings?.ecommerceName === 'MAGENTO' ||
                                                            product.salesChannel?.ecommerceSettings?.ecommerceName === 'PRESTA_SHOP') && (
                                                            <ButtonActions
                                                                key={'sync_button_' + product.modelID}
                                                                onClick={() => handleSyncWithEcommerceClick([product.id])}
                                                                data-testid={`sync-button-${product.modelID}`}
                                                            >
                                                                {T._('Sync image/name with e-commerce', 'Admin')}
                                                            </ButtonActions>
                                                        ),
                                                    isMarketplaceProduct(product) && (
                                                        <ButtonActions
                                                            key={'marketplace_preview_button_' + product.modelID}
                                                            onClick={() => handleMarketplacePreviewClick(product.id)}
                                                            disabled={product.statusID !== 1}
                                                            data-testid={`marketplace-preview-button-${product.modelID}`}
                                                        >
                                                            {T._('Generate a shareable demo product URL', 'Admin')}
                                                        </ButtonActions>
                                                    ),
                                                    product.zakekeEnvironment !== 'VIEWER' && (
                                                        <ButtonActionsDelete
                                                            key={'delete_button_' + product.modelID}
                                                            onClick={() => handleDeleteClick(product)}
                                                            data-testid={'delete_button_' + product.modelID}
                                                        >
                                                            {T._('Delete', 'Admin')}
                                                        </ButtonActionsDelete>
                                                    )
                                                ]}
                                                orientation={'RIGHT'}
                                            ></SelectActions>
                                        </ActionsColContainer>
                                    ];
                                },
                                (product: Model) => setSelectedProductId(product.id),
                                disableProviderProducts ? (product: Model) => DISABLED_PROVIDERS.has(product.provider) : undefined
                            )}
                        </Table>
                    )}
                </>
            )}
            {isPending ||
                (hasNext && isLoadingNext && (
                    <Center style={{ marginTop: '15px' }} data-testid='loading-more-products'>
                        <Loader />
                    </Center>
                ))}
            {(isPending || isInFightDelete || isInFightDeleteMassive || isInFightDuplicate || isInFightSync) && (
                <TableContentLoader data-testid='table-loading'></TableContentLoader>
            )}
            {selectedProductId && (
                <ProductSidePanel
                    productId={selectedProductId}
                    onClose={() => setSelectedProductId(null)}
                    onDelete={() => {
                        const p = getSelectedProduct();
                        setSelectedProductId(null);
                        if (p) handleDeleteDirect(p);
                    }}
                    onPreview={() => {
                        const p = getSelectedProduct();
                        if (p) handleMouseCickTest(p);
                    }}
                    onPublishToggle={() => {
                        const p = getSelectedProduct();
                        if (p) handleSetStatusClick(p);
                    }}
                    onDuplicate={() => {
                        const p = getSelectedProduct();
                        setSelectedProductId(null);
                        if (p) handleDuplicate(p);
                    }}
                    onAddSalesChannel={() => {
                        const p = getSelectedProduct();
                        if (p) handleAddSalesChannels(p);
                    }}
                    onToolAssignmentChange={() => startTransition(() => refetchProducts())}
                />
            )}
        </>
    );
};

export default ProductList;
